import React, { useState, useEffect } from "react";
import { Person, AccountBalance, Receipt, Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";
import EmailOtpModal from "../../components/EmailOtpModal";

const ClientTab = ({ loan }) => {
  const navigate = useNavigate();
  const client = loan?.client;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReversed, setShowReversed] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(client?.emailVerified || false);

  useEffect(() => {
    if (client?._id) {
      fetchSuspenseTransactions();
      fetchEmailVerificationStatus();
    }
  }, [client?._id]);

  const fetchEmailVerificationStatus = async () => {
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/email-verification-status/${client._id}`
      );
      if (response.ok) {
        const data = await response.json();
        setEmailVerified(data.emailVerified);
      }
    } catch (err) {
      console.error("Error fetching email verification status:", err);
    }
  };

  const fetchSuspenseTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_LOANS}/get-transaction`,
        { clientId: client._id }
      );

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.tranz || []);
      } else {
        setError("Failed to load transactions");
      }
    } catch (err) {
      console.error("Error fetching suspense transactions:", err);
      setError("An error occurred while loading transactions");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return "N/A";
    }
  };

  // Get primary contact info
  const getPrimaryPhone = () => {
    const phones = client?.contact?.phoneNumbers || [];
    const primary = phones.find(p => p.isPrimary);
    return primary?.phoneNumber || phones[0]?.phoneNumber || "N/A";
  };

  const getPrimaryEmail = () => {
    const emails = client?.contact?.emailAddresses || [];
    const primary = emails.find(e => e.isPrimary);
    return primary?.email || emails[0]?.email || "N/A";
  };

  const getPrimaryAddress = () => {
    const addresses = client?.contact?.addresses || [];
    const primary = addresses.find(a => a.isPrimary);
    const address = primary || addresses[0];
    if (!address) return "N/A";

    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.town,
      address.county,
      address.country
    ].filter(Boolean);

    return parts.join(", ") || "N/A";
  };

  // Calculate transaction movement
  const calculateMovement = (transaction) => {
    return (transaction.credit || 0) - (transaction.debit || 0);
  };

  // Filter transactions
  const filteredTransactions = showReversed
    ? transactions
    : transactions.filter(t => t.status !== 'Reversed');

  if (!client) {
    return (
      <div className="p-12 text-center">
        <Person className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Client Information</h3>
        <p className="text-gray-500">
          Client information is not available for this loan.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Biographic Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Person className="text-blue-600" />
          Biographic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Client Number</p>
            <p className="text-base font-semibold text-gray-900">
              {client.clientNumber || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Full Name</p>
            <p className="text-base font-semibold text-gray-900">
              {client.personal?.fullName || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">ID Number</p>
            <p className="text-base font-semibold text-gray-900">
              {client.personal?.idNumber || "N/A"}
              {client.personal?.idType && ` (${client.personal.idType})`}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Gender</p>
            <p className="text-base font-semibold text-gray-900">
              {client.personal?.gender
                ? client.personal.gender.charAt(0).toUpperCase() + client.personal.gender.slice(1).toLowerCase()
                : "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Date of Birth</p>
            <p className="text-base font-semibold text-gray-900">
              {formatDate(client.personal?.dateOfBirth)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Primary Phone</p>
            <p className="text-base font-semibold text-gray-900">
              {getPrimaryPhone()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Primary Email</p>
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-gray-900 break-all">
                {getPrimaryEmail()}
              </p>
              {getPrimaryEmail() !== "N/A" && (
                emailVerified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    <CheckCircle style={{ fontSize: 14 }} />
                    Verified
                  </span>
                ) : (
                  <button
                    onClick={() => setShowOtpModal(true)}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
                  >
                    Verify Email
                  </button>
                )
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-600 mb-1">Primary Address</p>
            <p className="text-base font-semibold text-gray-900">
              {getPrimaryAddress()}
            </p>
          </div>
        </div>
      </div>

      {/* Suspense Account Balance */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <AccountBalance className="text-blue-600" style={{ fontSize: 32 }} />
            <h3 className="text-lg font-bold text-gray-900">Suspense Account Balance</h3>
          </div>
          <p className="text-4xl font-bold text-blue-600 mt-2">
            {formatCurrency(client.suspenseAccountBalance || 0)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Available funds in client suspense account
          </p>
        </div>
      </div>

      {/* Suspense Transactions */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b p-4 bg-gray-50 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="text-blue-600" />
            Suspense Account Transactions ({filteredTransactions.length})
          </h3>
          {transactions.length > 0 && (
            <button
              onClick={() => setShowReversed(!showReversed)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {showReversed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              {showReversed ? 'Hide Reversed' : 'Show Reversed'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading transactions...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <Receipt className="mx-auto text-red-400 mb-4" style={{ fontSize: 64 }} />
            <h3 className="text-lg font-semibold text-red-700 mb-2">Error Loading Transactions</h3>
            <p className="text-red-500">{error}</p>
            <button
              onClick={fetchSuspenseTransactions}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Transactions</h3>
            <p className="text-gray-500">
              No suspense account transactions found for this client.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Movement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction, index) => {
                  const movement = calculateMovement(transaction);
                  const isReversed = transaction.status === 'Reversed';
                  const movementColor = isReversed
                    ? 'text-gray-400'
                    : movement >= 0
                    ? 'text-green-600'
                    : 'text-red-600';

                  return (
                    <tr
                      key={transaction._id || index}
                      className={`${isReversed ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {transaction.loanNumber ? (
                          <button
                            onClick={() => navigate(`/loan/${transaction.loanId}`)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {transaction.loanNumber}
                          </button>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {transaction.source || "N/A"}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-semibold ${movementColor} ${isReversed ? 'line-through' : ''}`}>
                        {movement >= 0 ? '+' : ''}{formatCurrency(movement)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isReversed ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Reversed
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Completed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Email OTP Verification Modal */}
      {showOtpModal && (
        <EmailOtpModal
          clientId={client._id}
          loanId={loan?._id}
          clientName={client.personal?.firstName}
          clientEmail={getPrimaryEmail()}
          onClose={() => setShowOtpModal(false)}
          onVerified={() => setEmailVerified(true)}
        />
      )}
    </div>
  );
};

export default ClientTab;
